import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { objectSerializer } from "helpers/utils";
import { toast } from "react-toastify";
import { 
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from 'helpers/reducerHelpers';

const baseUrl = "/crm";
const salesUrl = `${baseUrl}/release_forecast/sales`;
const csdUrl = `${baseUrl}/release_forecast/csds`;
const planningUrl = `${baseUrl}/release_forecast/plannings`;

// ==================== CSD Forecasts ====================
export const getCsdForecasts = createAsyncThunk(
  "ReleaseForecast/getCsdForecasts",
  async ({ params } = {}) => {
    try {
      const response = await apiAxios.get(csdUrl, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const getCsdPeriods = createAsyncThunk(
  "ReleaseForecast/getCsdPeriods",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${csdUrl}/periods`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const createCsdForecast = createAsyncThunk(
  "ReleaseForecast/createCsdForecast",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(csdUrl, { csd: data });
      toast.success("CSD Release Forecast created successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to create CSD Forecast");
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateCsdForecast = createAsyncThunk(
  "ReleaseForecast/updateCsdForecast",
  async ({ csdId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${csdUrl}/${csdId}`, { csd: data });
      toast.success("CSD Release Forecast updated successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update CSD Forecast");
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteCsdForecast = createAsyncThunk(
  "ReleaseForecast/deleteCsdForecast",
  async ({ csdIds }) => {
    try {
      await Promise.all(csdIds.map((id) => apiAxios.delete(`${csdUrl}/${id}`)));
      toast.success("Moved to Deleted — restore it from the Deleted tab");
      return csdIds;
    } catch (error) {
      toast.error(error.message);
      throw new Error(error.message);
    }
  }
);

export const getBuildingStatuses = createAsyncThunk(
  "ReleaseForecast/getBuildingStatuses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get('/crm/settings/building_statuses');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const deleteCsdForecastMonth = createAsyncThunk(
  "ReleaseForecast/deleteCsdForecastMonth",
  async ({ csdId, monthIndex, buildingId }, { rejectWithValue }) => {
    try {
      const body = { month_index: monthIndex };
      if (buildingId) body.building_id = buildingId;
      await apiAxios.patch(`${csdUrl}/${csdId}/remove_month`, body);
      toast.success("Moved to Deleted — restore it from the Deleted tab");
      return { csdId, monthIndex, buildingId };
    } catch (error) {
      toast.error(error.response?.data?.error || error.message);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const importCsdFromSource = createAsyncThunk(
  "ReleaseForecast/importCsdFromSource",
  async ({ period, items }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${csdUrl}/import`, { period, items });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const releaseCsdToPlanning = createAsyncThunk(
  "ReleaseForecast/releaseCsdToPlanning",
  async ({ period, csd_ids, ...email }, { rejectWithValue }) => {
    try {
      if (!period) {
        throw new Error('Period is required');
      }
      const response = await apiAxios.post(`${csdUrl}/release`, { period, csd_ids, ...email });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const backlogCsdForecast = createAsyncThunk(
  "ReleaseForecast/backlogCsdForecast",
  // silent: the caller shows its own toast (the row action offers Undo).
  async ({ id, silent = false }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${csdUrl}/${id}/backlog`);
      if (!silent) toast.success("Marked as backlog");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to mark as backlog");
      return rejectWithValue(error.response?.data);
    }
  }
);

// Undo of Move to backlog.
export const unbacklogCsdForecast = createAsyncThunk(
  "ReleaseForecast/unbacklogCsdForecast",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${csdUrl}/${id}/unbacklog`);
      toast.success("Moved back out of Backlog");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to undo");
      return rejectWithValue(error.response?.data);
    }
  }
);

// ==================== Release list ====================
// Projects CSD puts aside to release together — stored on the server, per period.
export const addToReleaseList = createAsyncThunk(
  "ReleaseForecast/addToReleaseList",
  async ({ csdIds }, { rejectWithValue }) => {
    try {
      const { data } = await apiAxios.post(`${csdUrl}/add_to_release_list`, { csd_ids: csdIds });
      toast.success(`Added ${data.added} project(s) to the Release list`
        + (data.skipped ? ` — ${data.skipped} released / backlog project(s) skipped` : ''));
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add to the Release list");
      return rejectWithValue(error.response?.data);
    }
  }
);

// { csdIds } removes those projects; { period, all: true } empties that period's list.
export const removeFromReleaseList = createAsyncThunk(
  "ReleaseForecast/removeFromReleaseList",
  async ({ csdIds, period, all }, { rejectWithValue }) => {
    try {
      const body = all ? { period, all: true } : { csd_ids: csdIds };
      const { data } = await apiAxios.post(`${csdUrl}/remove_from_release_list`, body);
      toast.success(`Removed ${data.removed} project(s) from the Release list`);
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update the Release list");
      return rejectWithValue(error.response?.data);
    }
  }
);

// Every forecast of one tab for a period, all pages — what a release confirms.
export const getCsdTabRecords = createAsyncThunk(
  "ReleaseForecast/getCsdTabRecords",
  async ({ period, state }, { rejectWithValue }) => {
    try {
      const { data } = await apiAxios.get(csdUrl, { params: { period, state, page: 1, per_page: 1000 } });
      return objectSerializer(data.rows || []) || [];
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to load the projects to release");
      return rejectWithValue(error.response?.data);
    }
  }
);

export const exportCsdForecasts = createAsyncThunk(
  "ReleaseForecast/exportCsdForecasts",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${csdUrl}/export`, {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `csd_release_forecast_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSD Forecast exported successfully");
      return true;
    } catch (error) {
      const msg = error.response?.data?.error || error.message || "Failed to export CSD Forecast";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateCsdMonthlies = createAsyncThunk(
  "ReleaseForecast/updateCsdMonthlies",
  async ({ csdId, monthlies }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${csdUrl}/${csdId}/update_monthlies`, { monthlies });
      toast.success("Monthly forecast saved");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save monthly forecast");
      return rejectWithValue(error.response?.data);
    }
  }
);

export const restoreCsdForecast = createAsyncThunk(
  "ReleaseForecast/restoreCsdForecast",
  async ({ csdId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${csdUrl}/${csdId}/restore`);
      toast.success("CSD Forecast restored successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to restore CSD Forecast");
      return rejectWithValue(error.response?.data);
    }
  }
);

// Grid rows → one call per forecast with the month rows they stand for. A row
// without months (a forecast that has none yet) stands for the forecast itself.
export const groupRowsByCsd = (rows) => Object.values(rows.reduce((acc, row) => {
  const csdId = row.csd_id || row.id;
  const entry = acc[csdId] || { csdId, monthlyIds: [] };
  return { ...acc, [csdId]: { ...entry, monthlyIds: [...new Set([...entry.monthlyIds, ...(row.monthly_ids || [])])] } };
}, {}));

const settle = async (items, call) => {
  const results = await Promise.allSettled(items.map(call));
  const failed = results.filter((r) => r.status === 'rejected');
  return { done: items.length - failed.length, error: failed[0]?.reason?.response?.data?.error };
};

const reportSettled = ({ done, error }, total, verb) => {
  if (done) toast.success(`${verb} ${done} of ${total} project(s)`);
  if (error) toast.error(error);
};

// Deletes exactly these rows (they wait in the Deleted tab).
export const deleteCsdRows = createAsyncThunk(
  "ReleaseForecast/deleteCsdRows",
  async ({ rows }) => {
    const items = groupRowsByCsd(rows);
    const result = await settle(items, ({ csdId, monthlyIds }) => (monthlyIds.length
      ? apiAxios.patch(`${csdUrl}/${csdId}/remove_month`, { monthly_ids: monthlyIds })
      : apiAxios.delete(`${csdUrl}/${csdId}`)));
    reportSettled(result, items.length, 'Moved to Deleted: rows of');
    return result;
  }
);

// Deleted tab: bring rows back, or remove them for good.
export const restoreCsdForecasts = createAsyncThunk(
  "ReleaseForecast/restoreCsdForecasts",
  async ({ rows }) => {
    const items = groupRowsByCsd(rows);
    const result = await settle(items, ({ csdId, monthlyIds }) =>
      apiAxios.post(`${csdUrl}/${csdId}/restore`, monthlyIds.length ? { monthly_ids: monthlyIds } : {}));
    reportSettled(result, items.length, 'Restored rows of');
    return result;
  }
);

export const purgeCsdForecasts = createAsyncThunk(
  "ReleaseForecast/purgeCsdForecasts",
  async ({ rows }) => {
    const items = groupRowsByCsd(rows);
    const result = await settle(items, ({ csdId, monthlyIds }) =>
      apiAxios.delete(`${csdUrl}/${csdId}/purge`, { data: monthlyIds.length ? { monthly_ids: monthlyIds } : {} }));
    reportSettled(result, items.length, 'Removed permanently: rows of');
    return result;
  }
);

// ==================== Sales Forecasts ====================
export const getSalesForecasts = createAsyncThunk(
  "ReleaseForecast/getSalesForecasts",
  async ({ params } = {}) => {
    try {
      const response = await apiAxios.get(salesUrl, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const createSalesForecast = createAsyncThunk(
  "ReleaseForecast/createSalesForecast",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(salesUrl, {
        sale: { ...data },
      });
      toast.success("Sales Release Forecast created successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to create Sales Forecast");
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateSalesForecast = createAsyncThunk(
  "ReleaseForecast/updateSalesForecast",
  async ({ saleId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${salesUrl}/${saleId}`, { sale: data });
      toast.success("Sales Forecast updated successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update Sales Forecast");
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteSalesForecast = createAsyncThunk(
  "ReleaseForecast/deleteSalesForecast",
  async ({ saleIds }) => {
    try {
      await Promise.all(
        saleIds.map((id) => apiAxios.delete(`${salesUrl}/${id}`))
      );
      toast.success("Sales Release Forecast(s) deleted successfully");
      return saleIds;
    } catch (error) {
      toast.error(error.message);
      throw new Error(error.message);
    }
  }
);

export const getSalesEmailConfig = createAsyncThunk(
  "ReleaseForecast/getSalesEmailConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${salesUrl}/email_config`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const releaseSalesToCsd = createAsyncThunk(
  "ReleaseForecast/releaseSalesToCsd",
  async (payload, { rejectWithValue }) => {
    try {
      const toEmails = payload?.toEmails;
      const ccEmails = payload?.ccEmails;
      const saleIds  = payload?.saleIds;
      const subject  = payload?.subject;
      const body     = payload?.body;

      if (!saleIds || saleIds.length === 0) {
        return rejectWithValue({ success: false, message: 'No records selected', error: 'NO_IDS' });
      }

      const response = await apiAxios.post(`${salesUrl}/release`, {
        sale_ids:  saleIds,
        to_emails: toEmails,
        cc_emails: ccEmails,
        subject,
        body,
      });
      const data = response.data;

      // Handle success response
      if (data?.success) {
        toast.success(data.message || "Sales Forecast sent to CSD successfully");
        return data;
      } else {
        // Backend returned error but with 2xx status (shouldn't happen with current controller)
        console.warn('⚠️ Unexpected success status with error flag:', data);
        return rejectWithValue(data);
      }
    } catch (error) {
      console.error('❌ Release Sales to CSD failed:', error);
      
      // Extract error response from API
      const errorData = error.response?.data;
      
      if (errorData?.success === false) {
        // API returned structured error response
        const errorMessage = errorData.message || errorData.error || "Failed to release to CSD";
        const errorCode = errorData.error;
        
        // Show different toasts based on error type
        if (errorCode === 'ALREADY_RELEASED') {
          toast.warning(errorMessage);
        } else if (errorCode === 'NO_RECORDS') {
          toast.info(errorMessage);
        } else {
          toast.error(errorMessage);
        }
        
        return rejectWithValue(errorData);
      } else {
        // Fallback for network errors or other issues
        const fallbackMessage = error.message || "Failed to release to CSD";
        toast.error(fallbackMessage);
        return rejectWithValue({
          success: false,
          message: fallbackMessage,
          error: error.response?.status ? `HTTP_${error.response.status}` : 'NETWORK_ERROR'
        });
      }
    }
  }
);

export const exportSalesForecast = createAsyncThunk(
  "ReleaseForecast/exportSalesForecast",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${salesUrl}/export`, {
        params,
        responseType: "blob",
      });
      
      // response.data is already a blob, no need to wrap it again
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `sales_release_forecast_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Sales Forecast exported successfully");
      return true;
    } catch (error) {
      console.error('Export error details:', error);
      let errorMsg = "Failed to export Sales Forecast";
      
      if (error.response?.status === 401) {
        errorMsg = "Unauthorized: Please login again";
      } else if (error.response?.status === 403) {
        errorMsg = "Forbidden: You don't have permission to export";
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// ==================== Eligible Buildings (global) ====================
export const getEligibleBuildings = createAsyncThunk(
  "ReleaseForecast/getEligibleBuildings",
  async (params = {}) => {
    try {
      const response = await apiAxios.get(
        `${baseUrl}/release_forecast/sales/buildings_list`,
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const recalculateCsdForecast = createAsyncThunk(
  "ReleaseForecast/recalculateCsdForecast",
  async ({ csdId, monthIndex, buildingId, source } = {}) => {
    try {
      const body = {};
      if (monthIndex)  body.month_index  = monthIndex;
      if (buildingId)  body.building_id  = buildingId;
      if (source)      body.source       = source;
      const response = await apiAxios.post(
        `${csdUrl}/${csdId}/recalculate`,
        Object.keys(body).length ? body : undefined
      );
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const recalculateCsdPeriod = createAsyncThunk(
  "ReleaseForecast/recalculateCsdPeriod",
  async ({ period } = {}) => {
    try {
      const body = period ? { period } : {};
      const response = await apiAxios.post(`${csdUrl}/recalculate_period`, body);
      return response.data; // { task_id, status: 'pending' }
    } catch (error) {
      throw new Error(error?.response?.data?.error || error.message);
    }
  }
);

export const getCsdRecalculateStatus = createAsyncThunk(
  "ReleaseForecast/getCsdRecalculateStatus",
  async ({ taskId }, { rejectWithValue }) => {
    try {
      // skipErrorToast suppresses the global apiAxios 404 interceptor toast
      // so polling doesn't flood the UI with "Resource not found" messages.
      const response = await apiAxios.get(
        `${csdUrl}/recalculate_period_status`,
        { params: { task_id: taskId }, skipErrorToast: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error.message);
    }
  }
);

export const getPlanningForecast = createAsyncThunk(
  "ReleaseForecast/getPlanningForecast",
  async ({ params } = {}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(planningUrl, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const getPlanningEmailConfig = createAsyncThunk(
  "ReleaseForecast/getPlanningEmailConfig",
  async ({ target = 'production' } = {}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${planningUrl}/email_config`, { params: { target } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const releasePlanningToProduction = createAsyncThunk(
  "ReleaseForecast/releasePlanningToProduction",
  async ({ period, to_emails, cc_emails, subject, body }, { rejectWithValue }) => {
    try {
      if (!period) {
        throw new Error('Period is required');
      }
      const response = await apiAxios.post(`${planningUrl}/release`, { period, to_emails, cc_emails, subject, body });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const getCsdReleaseEmailConfig = createAsyncThunk(
  'ReleaseForecast/getCsdReleaseEmailConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${csdUrl}/email_config`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const requestPlanningForecastMaterials = createAsyncThunk(
  'ReleaseForecast/requestPlanningForecastMaterials',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${planningUrl}/request_materials`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const recalculatePlanningFromDocuments = createAsyncThunk(
  'ReleaseForecast/recalculatePlanningFromDocuments',
  async ({ csd_ids }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${planningUrl}/recalculate_from_documents`, { csd_ids });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const exportPlanningForecast = createAsyncThunk(
  "ReleaseForecast/exportPlanningForecast",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${planningUrl}/export`, {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Planning_Forecast_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Planning Report exported successfully");
      return true;
    } catch (error) {
      const msg = error.response?.data?.error || error.message || "Failed to export Planning Report";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// ==================== Slice ====================
const releaseForecastSlice = createSlice({
  name: "crm_release_forecast",
  initialState: {
    csdData: { ...initialState },
    salesData: { ...initialState },
    csdMonthlies: { ...initialState },
    salesMonthlies: { ...initialState },
    buildingsData: { ...initialState },
    csdReportData: { ...initialState },
    planningReportData: { ...initialState },
    exportLoading: false,
    exportError: null,
  },
  reducers: {
    // CSD Reducers
    setCsdPage: (state, action) => setNestedPageHelper(state.csdData, action),
    setCsdPageSize: (state, action) => setNestedPageSizeHelper(state.csdData, action),
    setCsdParams: (state, action) => setNestedParamsHelper(state.csdData, action),
    resetCsdParams: (state) => resetNestedParamsHelper(state.csdData),
    setCsdSort: (state, action) => setSortParamsHelper(state.csdData, action),
    setCsdFilterTag: (state, action) => setFilterTagHelper(state.csdData, action),
    resetCsdFilterTag: (state) => resetFilterTagHelper(state.csdData),

    // Sales Reducers
    setSalesPage: (state, action) => setNestedPageHelper(state.salesData, action),
    setSalesPageSize: (state, action) => setNestedPageSizeHelper(state.salesData, action),
    setSalesParams: (state, action) => setNestedParamsHelper(state.salesData, action),
    resetSalesParams: (state) => resetNestedParamsHelper(state.salesData),
    setSalesSort: (state, action) => setSortParamsHelper(state.salesData, action),
    setSalesFilterTag: (state, action) => setFilterTagHelper(state.salesData, action),
    resetSalesFilterTag: (state) => resetFilterTagHelper(state.salesData),

    // Report Reducers
    setCsdReportPage: (state, action) => setNestedPageHelper(state.csdReportData, action),
    setCsdReportPageSize: (state, action) => setNestedPageSizeHelper(state.csdReportData, action),
    setPlanningReportPage: (state, action) => setNestedPageHelper(state.planningReportData, action),
    setPlanningReportPageSize: (state, action) => setNestedPageSizeHelper(state.planningReportData, action),
  },
  extraReducers: (builder) => {
    // ==================== CSD Forecasts ====================
    // Only the latest request may fill the grid: typing a search fires one
    // request per pause, and an older, slower answer used to land last and put
    // back the previous filter's rows.
    builder.addCase(getCsdForecasts.pending, (state, action) => {
      state.csdData.loading = true;
      state.csdData.latestRequestId = action.meta.requestId;
    });
    builder.addCase(getCsdForecasts.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.csdData.latestRequestId) return;
      handleFulfilled(state.csdData, action, "allData");
      // Sent with page 1 only; later pages keep the counts already shown.
      if (action.payload?.tab_counts) state.csdData.tabCounts = action.payload.tab_counts;
    });
    builder.addCase(getCsdForecasts.rejected, (state, action) => {
      if (action.meta.requestId !== state.csdData.latestRequestId) return;
      state.csdData.loading = false;
      toast.error(action.error?.message || "Failed to load CSD Forecasts");
    });

    builder.addCase(createCsdForecast.fulfilled, (state, action) => {
      handlePayload(state.csdData, action, "create");
    });

    builder.addCase(updateCsdForecast.fulfilled, (state, action) => {
      handlePayload(state.csdData, action);
    });

    builder.addCase(deleteCsdForecast.fulfilled, (state, action) => {
      action.payload.forEach((id) => {
        const index = state.csdData.allData.data.findIndex((item) => item.id === id);
        if (index !== -1) {
          state.csdData.allData.data.splice(index, 1);
        }
      });
    });

    builder.addCase(releaseCsdToPlanning.fulfilled, (state, action) => {
      handlePayload(state.csdData, action);
    });

    builder.addCase(updateCsdMonthlies.fulfilled, (state, action) => {
      handlePayload(state.csdData, action);
    });

    builder.addCase(restoreCsdForecast.fulfilled, (state, action) => {
      handlePayload(state.csdData, action);
    });

    builder.addCase(recalculateCsdForecast.fulfilled, (state, action) => {
      handlePayload(state.csdData, action);
    });

    // ==================== Sales Forecasts ====================
    builder.addCase(getSalesForecasts.pending, (state) => {
      state.salesData.loading = true;
    });
    builder.addCase(getSalesForecasts.fulfilled, (state, action) => {
      handleFulfilled(state.salesData, action, "allData");
    });
    builder.addCase(getSalesForecasts.rejected, (state, action) => {
      state.salesData.loading = false;
      toast.error(action.error?.message || "Failed to load Sales Forecasts");
    });

    builder.addCase(createSalesForecast.fulfilled, (state, action) => {
      handlePayload(state.salesData, action, "create");
    });

    builder.addCase(updateSalesForecast.fulfilled, (state, action) => {
      handlePayload(state.salesData, action);
    });

    builder.addCase(deleteSalesForecast.fulfilled, (state, action) => {
      action.payload.forEach((id) => {
        const index = state.salesData.allData.data.findIndex((item) => item.id === id);
        if (index !== -1) {
          state.salesData.allData.data.splice(index, 1);
        }
      });
    });

    builder.addCase(releaseSalesToCsd.fulfilled, (state) => {
      // Success response: { success: true, message, record_count, released_at, released_by }
      // Component handles reload() after this
      state.salesData.loading = false;
    });

    builder.addCase(releaseSalesToCsd.rejected, (state) => {
      // Error response handled by thunk with appropriate toast
      state.salesData.loading = false;
    });

    // ==================== CSD Export ====================
    builder.addCase(exportCsdForecasts.pending, (state) => {
      state.exportLoading = true;
    });
    builder.addCase(exportCsdForecasts.fulfilled, (state) => {
      state.exportLoading = false;
    });
    builder.addCase(exportCsdForecasts.rejected, (state) => {
      state.exportLoading = false;
    });

    // ==================== Export ====================
    builder.addCase(exportSalesForecast.pending, (state) => {
      state.exportLoading = true;
      state.exportError = null;
    });

    builder.addCase(exportSalesForecast.fulfilled, (state) => {
      state.exportLoading = false;
      state.exportError = null;
    });

    builder.addCase(exportSalesForecast.rejected, (state, action) => {
      state.exportLoading = false;
      state.exportError = action.payload || "Failed to export";
    });

    // ==================== Monthly Records ====================
    builder.addCase(getEligibleBuildings.pending, (state) => {
      state.buildingsData.loading = true;
    });
    builder.addCase(getEligibleBuildings.fulfilled, (state, action) => {
      handleFulfilled(state.buildingsData, action, "allData");
    });
    builder.addCase(getEligibleBuildings.rejected, (state, action) => {
      state.buildingsData.loading = false;
      toast.error(action.error?.message || "Failed to load eligible buildings");
    });

    // ==================== Planning Report ====================
    builder.addCase(getPlanningForecast.pending, (state) => {
      state.planningReportData.loading = true;
    });
    builder.addCase(getPlanningForecast.fulfilled, (state, action) => {
      state.planningReportData.loading = false;
      state.planningReportData.allData = action.payload;
    });
    builder.addCase(getPlanningForecast.rejected, (state, action) => {
      state.planningReportData.loading = false;
      toast.error(action.payload?.error || "Failed to load Planning Report");
    });

    builder.addCase(releasePlanningToProduction.fulfilled, (state) => {
      // Success response: { success: true, message, record_count, released_at, released_by }
      // Component handles reload() after this
      state.planningReportData.loading = false;
    });

    builder.addCase(releasePlanningToProduction.rejected, (state) => {
      // Error response handled by thunk with appropriate toast
      state.planningReportData.loading = false;
    });

    builder.addCase(exportPlanningForecast.pending, (state) => { state.exportLoading = true; });
    builder.addCase(exportPlanningForecast.fulfilled, (state) => { state.exportLoading = false; });
    builder.addCase(exportPlanningForecast.rejected, (state) => { state.exportLoading = false; });
  },
});

export const {
  setCsdPage,
  setCsdPageSize,
  setCsdParams,
  resetCsdParams,
  setCsdSort,
  setCsdFilterTag,
  resetCsdFilterTag,
  setSalesPage,
  setSalesPageSize,
  setSalesParams,
  resetSalesParams,
  setSalesSort,
  setSalesFilterTag,
  resetSalesFilterTag,
  setCsdReportPage,
  setCsdReportPageSize,
  setPlanningReportPage,
  setPlanningReportPageSize,
} = releaseForecastSlice.actions;

export default releaseForecastSlice.reducer;
